// use-tags.ts — Tags suite, public hooks.

'use client';

import { useCallback } from 'react';
import type { TagsListResponse } from '@cabal/shared/domain/session/team/tag/types.js';
import { deriveResult, type QueryResult } from '@/types/async-protocol';
import { useTagsContext } from './TagsProvider';

type PublicTag = TagsListResponse['tags'][number];

export function useTags(): QueryResult<PublicTag[]> {
  return useTagsContext().tagsQuery;
}

export function useTagById(): (tagId: string) => PublicTag | null;
export function useTagById(tagId: string | null | undefined): PublicTag | null;
export function useTagById(
  tagId?: string | null,
): PublicTag | null | ((tagId: string) => PublicTag | null) {
  const { tagsById } = useTagsContext();

  const getTagById = useCallback((inputTagId: string): PublicTag | null => {
    return tagsById.get(inputTagId) ?? null;
  }, [tagsById]);

  if (tagId === undefined) {
    return getTagById;
  }

  return tagId ? getTagById(tagId) : null;
}

export function useTagByName(): (tagName: string) => PublicTag | null;
export function useTagByName(tagName: string | null | undefined): PublicTag | null;
export function useTagByName(
  tagName?: string | null,
): PublicTag | null | ((tagName: string) => PublicTag | null) {
  const { tagsByName } = useTagsContext();

  const getTagByName = useCallback((inputTagName: string): PublicTag | null => {
    return tagsByName.get(inputTagName) ?? null;
  }, [tagsByName]);

  if (tagName === undefined) {
    return getTagByName;
  }

  return tagName ? getTagByName(tagName) : null;
}

export function useTagNamesByIds(): (tagIds: string[] | null | undefined) => string[] {
  const getTagById = useTagById();

  return useCallback((tagIds: string[] | null | undefined): string[] => {
    return (tagIds ?? [])
      .map((tagId) => getTagById(tagId)?.name)
      .filter((tagName): tagName is string => Boolean(tagName));
  }, [getTagById]);
}
